import React, { useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import Modal, { ModalConfirmation } from '@adsk/alloy-react-modal';
import { COMPONENT_VARIANTS } from '@common/constants';
import { useCreateAccountCompanyValidation } from '@common/hooks';
import { useStore } from '@common/StoreProvider';
import Footer from './components/Footer';
import EmailField from './components/EmailField';
import CompanyField from './components/CompanyField';
import RoleField from './components/RoleField';
import { ACTIONS } from './state/actions';
import { simpleModalStyle, modalBodyStyle } from './styles';

/**
 * A InviteMemberModal
 * docs.
 */

const InviteMemberModal = ({
  accountUsers,
  allowReinviting,
  children,
  companies,
  cancelInviteConfirmationProps,
  checkDataEntered,
  disableInvite,
  modalConfirmButtonText,
  modalTitle,
  onChangeCompanyInput,
  onChangeEmailInput,
  onChangeRoleInput,
  onClose,
  onOpen,
  onSubmitInviteMembers,
  roles,
  show,
  projectUsers,
  variant,
}) => {
  const { state, dispatch } = useStore();
  const isEmptyEmailField = state.emails.length === 0;
  const isInvalidEmailField =
    state.numberOfInvalidEmails + state.numberOfDuplicatedEmails > 0;
  const canCreateCompanies = useCreateAccountCompanyValidation();

  const latestDataEntered = useRef(checkDataEntered);

  const showMainModal = (value) => {
    dispatch({
      type: ACTIONS.SHOW_MAIN_MODAL,
      payload: value,
    });
    if (value) {
      onOpen();
    }
  };

  const showConfirmationModal = (value) => {
    dispatch({
      type: ACTIONS.SHOW_CONFIRMATION_MODAL,
      payload: value,
    });
  };

  const resetModalInformation = () => {
    dispatch({
      type: ACTIONS.RESET_INFORMATION,
    });
  };

  const handleOnCancelClick = useCallback(() => {
    if (
      (state.selectedCompany &&
        state.selectedCompany.id !== 'UNSELECTED_OPTION') ||
      (state.selectedRole && state.selectedRole.id !== 'UNSELECTED_OPTION') ||
      !isEmptyEmailField ||
      (!!latestDataEntered.current && latestDataEntered.current())
    ) {
      showMainModal(false);
      showConfirmationModal(true);
    } else {
      showMainModal(false);
      resetModalInformation();
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEmptyEmailField, onClose, state.selectedCompany, state.selectedRole]);

  const handleConfirmationBackClick = () => {
    showMainModal(true);
    showConfirmationModal(false);
  };

  const handleConfirmationCancelClick = () => {
    showMainModal(false);
    resetModalInformation();
    showConfirmationModal(false);
    onClose();
  };

  useEffect(() => {
    latestDataEntered.current = checkDataEntered;
  }, [checkDataEntered]);

  useEffect(() => {
    if (state.areUsersInvited) {
      resetModalInformation();
      dispatch({
        type: ACTIONS.SET_ARE_USERS_INVITED,
        payload: false,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.areUsersInvited]);

  useEffect(() => {
    showMainModal(show);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  return (
    <>
      <Modal
        onClose={handleOnCancelClick}
        open={state.showMainModal}
        style={simpleModalStyle}
        shouldCloseOnEsc
        shouldCloseOnOverlayClick={false}
      >
        <Modal.Header>
          {modalTitle}
          <Modal.Close onClick={handleOnCancelClick} />
        </Modal.Header>
        <Modal.Body
          data-testid="InviteProjectMemberModal_body"
          style={modalBodyStyle}
        >
          <EmailField
            accountUsers={accountUsers}
            allowReinviting={allowReinviting}
            isInvalidEmailField={isInvalidEmailField}
            onChangeEmailInput={onChangeEmailInput}
            projectUsers={projectUsers}
            variant={variant}
          />
          {variant !== COMPONENT_VARIANTS.TRIAL && (
            <>
              <CompanyField
                canCreateCompanies={canCreateCompanies}
                companyCreateError={state.companyCreateError}
                companies={companies}
                onChangeCompanyInput={onChangeCompanyInput}
                variant={variant}
              />
              <RoleField
                roles={roles}
                onChangeRoleInput={onChangeRoleInput}
                variant={variant}
              />
            </>
          )}
          {children}
        </Modal.Body>
        <Modal.Footer>
          <Footer
            onCancelClick={handleOnCancelClick}
            onInviteClick={onSubmitInviteMembers}
            inviteDisabled={
              isEmptyEmailField || isInvalidEmailField || disableInvite
            }
            confirmButtonText={modalConfirmButtonText}
            isSaving={state.isSaving}
          />
        </Modal.Footer>
      </Modal>
      <ModalConfirmation
        data-testid="InviteProjectMemberModal_confirmation_modal"
        show={state.showConfirmationModal}
        title={cancelInviteConfirmationProps.title}
        message={cancelInviteConfirmationProps.message}
        cancelButtonLabel={cancelInviteConfirmationProps.cancelButtonLabel}
        confirmationButtonLabel={
          cancelInviteConfirmationProps.confirmationButtonLabel
        }
        onExited={handleConfirmationBackClick}
        onClose={handleConfirmationBackClick}
        onConfirm={handleConfirmationCancelClick}
        backdrop
        disableModalExit
        isConfirmationButtonDisabled={false}
        isOnConfirmInProgress={false}
        hideOnConfirm={false}
        noCancelButton={false}
      />
    </>
  );
};

InviteMemberModal.displayName = 'InviteMemberModal';
InviteMemberModal.propTypes = {
  /** Account Users list */
  accountUsers: PropTypes.arrayOf(PropTypes.object).isRequired,
  /** Indicates if reinviting users is enabled */
  allowReinviting: PropTypes.bool,
  cancelInviteConfirmationProps: PropTypes.shape({
    cancelButtonLabel: PropTypes.string,
    confirmationButtonLabel: PropTypes.string,
    message: PropTypes.string,
    title: PropTypes.string,
  }),
  children: PropTypes.node.isRequired,
  /** Companies list */
  companies: PropTypes.arrayOf(PropTypes.object),
  checkDataEntered: PropTypes.func,
  disableInvite: PropTypes.bool,
  modalConfirmButtonText: PropTypes.string.isRequired,
  modalTitle: PropTypes.string.isRequired,
  /** handler excecuted to invite members */
  onSubmitInviteMembers: PropTypes.func.isRequired,
  /** handler executed after the user cancels an invite a project member */
  onClose: PropTypes.func.isRequired,
  /** handler executed when a email input changes */
  onChangeEmailInput: PropTypes.func.isRequired,
  /** handler executed when a company input changes */
  onChangeCompanyInput: PropTypes.func.isRequired,
  /** handler executed when a role input changes */
  onChangeRoleInput: PropTypes.func.isRequired,
  /** handler executed when the modal opens */
  onOpen: PropTypes.func,
  /** Project Users List */
  projectUsers: PropTypes.arrayOf(PropTypes.object).isRequired,
  /** Roles list */
  roles: PropTypes.arrayOf(PropTypes.object),
  /** Boolean property to set the visibility of the Modal */
  show: PropTypes.bool,
  variant: PropTypes.oneOf(Object.values(COMPONENT_VARIANTS)),
};

InviteMemberModal.defaultProps = {
  allowReinviting: false,
  disableInvite: false,
  cancelInviteConfirmationProps: {},
  checkDataEntered: null,
  companies: [],
  onOpen: () => {},
  roles: [],
  show: false,
  variant: COMPONENT_VARIANTS.PROJECT,
};

export default InviteMemberModal;
